import React, { useEffect, useMemo, useState } from 'react';
import { EnvelopeIcon } from '../../../components/ui/Icons';
import { Button, ConfirmationDialog, AlertDialog } from '../../../components/ui';
import { API_BASE_URL } from '../../../config/apiConfig';

async function readSettingBoolean(key, defaultValue = true) {
  const token = localStorage.getItem('authToken');
  const response = await fetch(
    `${API_BASE_URL}/application-setting/key/${encodeURIComponent(key)}`,
    {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!response.ok) return defaultValue;
  const setting = await response.json();
  if (setting == null || setting.value == null || setting.value === '') {
    return defaultValue;
  }
  return String(setting.value).trim().toLowerCase() === 'true';
}

function userLabel(user, fallbackId) {
  // GenericModulePage may replace relation objects with display strings.
  if (typeof user === 'string' && user.trim()) return user.trim();
  if (!user || typeof user !== 'object') {
    return fallbackId ? `User #${fallbackId}` : '';
  }
  return (
    user.userDisplayName ||
    [user.userFirstName, user.userLastName].filter(Boolean).join(' ') ||
    user.userEmail ||
    (user.id ? `User #${user.id}` : fallbackId ? `User #${fallbackId}` : '')
  );
}

/**
 * Assignees for confirm UI — based on IDs/relations, not client-side email.
 * Backend resolves emails when sending.
 */
function collectAssignees(record) {
  const byKey = new Map();

  const add = (user, id, roleLabel) => {
    const resolvedId =
      (user && typeof user === 'object' ? user.id : null) ?? id ?? null;
    const hasUser = Boolean(
      resolvedId != null ||
        (typeof user === 'string' && user.trim()) ||
        (user && typeof user === 'object'),
    );
    if (!hasUser) return;
    const key = String(resolvedId ?? userLabel(user, id));
    const existing = byKey.get(key);
    if (existing) {
      if (!existing.roles.includes(roleLabel)) existing.roles.push(roleLabel);
      return;
    }
    byKey.set(key, {
      id: resolvedId,
      name: userLabel(user, resolvedId),
      email:
        user && typeof user === 'object' ? user.userEmail?.trim() || '' : '',
      roles: [roleLabel],
    });
  };

  add(record?.inspectedBy, record?.inspectedById, 'Inspected By');
  add(record?.dispositionBy, record?.dispositionById, 'Disposition By');
  return [...byKey.values()];
}

const NotifyForSignatureButton = ({ record, onSuccess, onError }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAlert, setShowAlert] = useState({
    show: false,
    message: '',
    type: 'error',
  });
  const [isSystemEnabled, setIsSystemEnabled] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const assignees = useMemo(() => collectAssignees(record), [record]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const [systemEnabled, rpiEnabled] = await Promise.all([
          readSettingBoolean('emailNotificationSystemEnabled'),
          readSettingBoolean('emailNotificationRpiEnabled'),
        ]);
        if (!cancelled) {
          setIsSystemEnabled(systemEnabled && rpiEnabled);
          setSettingsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setIsSystemEnabled(true);
          setSettingsLoaded(true);
        }
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasAssignees = assignees.length > 0;
  // Only hard-disable while sending; other blocks stay clickable so user sees why.
  const isHardDisabled = !record?.id || isSending;

  const handleClick = () => {
    if (isHardDisabled) return;

    if (settingsLoaded && !isSystemEnabled) {
      setShowAlert({
        show: true,
        message:
          'Email notifications are disabled. Enable emailNotificationSystemEnabled and emailNotificationRpiEnabled in Application Settings.',
        type: 'warning',
      });
      return;
    }

    if (!hasAssignees) {
      setShowAlert({
        show: true,
        message:
          'Assign Inspected By and/or Disposition By before notifying.',
        type: 'info',
      });
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setIsSending(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/returned-product-inspection/${record.id}/notify-for-signature`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const raw = payload?.message;
        const message = Array.isArray(raw)
          ? raw.join(', ')
          : raw || 'Failed to send signature notification emails.';
        throw new Error(String(message));
      }

      const count = payload?.sentCount ?? assignees.length;
      setShowAlert({
        show: true,
        message: `Signature notification sent to ${count} recipient${count === 1 ? '' : 's'}.`,
        type: 'success',
      });
      onSuccess?.(payload);
    } catch (error) {
      setShowAlert({
        show: true,
        message: error.message || 'Failed to send signature notification emails.',
        type: 'error',
      });
      onError?.(error);
    } finally {
      setIsSending(false);
    }
  };

  const assigneeSummary = assignees
    .map((r) => `${r.name} (${r.roles.join(' & ')})`)
    .join(', ');

  const looksInactive =
    isHardDisabled ||
    (settingsLoaded && !isSystemEnabled) ||
    !hasAssignees;

  const tooltipText =
    settingsLoaded && !isSystemEnabled
      ? 'Email notification system is disabled'
      : !hasAssignees
        ? 'No Inspected By / Disposition By assigned'
        : `Notify for signature: ${assigneeSummary}`;

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={isHardDisabled}
        variant="outline"
        size="sm"
        title={tooltipText}
        className={looksInactive && !isHardDisabled ? 'opacity-50' : undefined}
      >
        <EnvelopeIcon className="h-4 w-4" />
        <span>Notify for signature</span>
      </Button>

      <ConfirmationDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        title="Notify for signature"
        message={`Send signature notification email to: ${assigneeSummary}?`}
        confirmText="Send"
        cancelText="Cancel"
        isLoading={isSending}
      />

      <AlertDialog
        isOpen={showAlert.show}
        onClose={() => setShowAlert({ show: false, message: '', type: 'error' })}
        title=""
        message={showAlert.message}
        type={showAlert.type}
        confirmText="OK"
      />
    </>
  );
};

export default NotifyForSignatureButton;
